import { LOCALE_ORDER, type Locale, type LocalizedString } from "./constants";
import {
  baseLocaleCode,
  getStoredFireCMSLocale,
} from "./getStoredFireCMSLocale";

function stringValue(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** True when the map has at least one non-empty string translation. */
export function hasLocalizedStringValue(
  localized: LocalizedString | undefined | null
): boolean {
  if (!localized || typeof localized !== "object") {
    return false;
  }

  return Object.values(localized).some(
    (value) => stringValue(value) !== undefined
  );
}

/**
 * Resolves display text from a localized map (e.g. for admin preview or app default).
 * Priority: FireCMS UI locale → preferredLocale (e.g. event.mainLocale) → fallbackOrder → first non-empty value.
 */
export function resolveLocalizedString(
  localized: LocalizedString | undefined,
  preferredLocale?: string,
  fallbackOrder: readonly Locale[] = LOCALE_ORDER,
  cmsLocale?: string
): string {
  if (!localized) {
    return "";
  }

  const uiLocale = cmsLocale ?? getStoredFireCMSLocale();
  if (uiLocale) {
    const fromUiLocale = stringValue(
      localized[baseLocaleCode(uiLocale)]
    );
    if (fromUiLocale) {
      return fromUiLocale;
    }
  }

  const preferred = preferredLocale
    ? stringValue(localized[preferredLocale])
    : undefined;
  if (preferred) {
    return preferred;
  }

  for (const locale of fallbackOrder) {
    const value = stringValue(localized[locale]);
    if (value) {
      return value;
    }
  }

  for (const value of Object.values(localized)) {
    const resolved = stringValue(value);
    if (resolved) {
      return resolved;
    }
  }

  return "";
}
